<template>
  <div class="container-fluid">
    <div class="mb-4">
      <h5 class="mb-0 font-weight-bold page-title">Management</h5>
      <p class="text-secondary text-sm mb-0">
        งานของสมาชิกในทีม — Plan, Task และ Test Run ที่มอบหมาย
      </p>
    </div>

    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
      <p class="text-secondary text-sm mt-2 mb-0">Loading team tasks...</p>
    </div>

    <div v-else-if="loadError" class="text-center py-5">
      <i class="fas fa-exclamation-triangle fa-2x text-warning opacity-6"></i>
      <p class="text-secondary text-sm mt-3 mb-2">{{ loadError }}</p>
      <button type="button" class="btn btn-sm btn-outline-primary mb-0" @click="loadTeamTasks">
        Retry
      </button>
    </div>

    <div v-else class="card mb-3 mgr-shell-card">
      <div class="card-header pb-0">
        <div class="tab-shell" role="tablist" aria-label="Management view">
          <div
            class="tab-item"
            :class="{ active: activeView === 'sprint' }"
            role="tab"
            :aria-selected="activeView === 'sprint'"
            @click="activeView = 'sprint'"
          >
            BY SPRINT
          </div>
          <div
            class="tab-item"
            :class="{ active: activeView === 'person' }"
            role="tab"
            :aria-selected="activeView === 'person'"
            @click="activeView = 'person'"
          >
            BY PERSON
          </div>
          <div
            class="tab-item"
            :class="{ active: activeView === 'calendar' }"
            role="tab"
            :aria-selected="activeView === 'calendar'"
            @click="activeView = 'calendar'"
          >
            CALENDAR
          </div>
        </div>
      </div>

      <div class="card-body mgr-shell-content">
      <div class="mgr-filters-row mb-3">
        <div v-if="activeView === 'calendar'" class="stat-col stat-col-filter">
          <div class="stat-card filter-card">
            <div class="filter-content">
              <p class="stat-label">View as</p>
              <div class="mgr-scope-toggle" role="tablist" aria-label="Calendar scope">
                <button
                  type="button"
                  class="mgr-scope-btn"
                  :class="{ active: calendarScope === 'team' }"
                  @click="calendarScope = 'team'"
                >
                  Team
                </button>
                <button
                  type="button"
                  class="mgr-scope-btn"
                  :class="{ active: calendarScope === 'person' }"
                  @click="calendarScope = 'person'"
                >
                  Person
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="showTeamFilter" class="stat-col stat-col-filter">
          <div class="stat-card filter-card team-card">
            <div class="filter-content">
              <p class="stat-label">Team</p>
              <base-select
                size="sm"
                :model-value="selectedSprintTeam"
                :options="teamDropdownOptions"
                option-label="label"
                option-value="value"
                placeholder="All teams"
                @update:model-value="switchSprintTeam"
              />
            </div>
          </div>
        </div>
        <div v-if="showPersonFilters" class="stat-col stat-col-filter">
          <div class="stat-card filter-card team-card">
            <div class="filter-content">
              <p class="stat-label">Team</p>
              <base-select
                size="sm"
                :model-value="effectivePersonTeam"
                :options="personTeamOptions"
                option-label="label"
                option-value="value"
                :disabled="personTeamLocked"
                @update:model-value="switchPersonTeam"
              />
            </div>
          </div>
        </div>
        <div v-if="showPersonFilters" class="stat-col stat-col-filter">
          <div class="stat-card filter-card">
            <div class="filter-content">
              <p class="stat-label">View tasks for</p>
              <base-select
                size="sm"
                :model-value="selectedMemberId"
                :options="memberDropdownOptions"
                option-label="label"
                option-value="value"
                placeholder="All members"
                @update:model-value="selectedMemberId = $event"
              />
            </div>
          </div>
        </div>
        <div v-if="activeView === 'sprint' || activeView === 'person' || activeView === 'calendar'" class="stat-col stat-col-filter">
          <div class="stat-card filter-card sprint-card">
            <div class="filter-content">
              <p class="stat-label">Sprint</p>
              <base-select
                size="sm"
                :model-value="selectedSprintId"
                :options="sprintDropdownOptions"
                option-label="label"
                option-value="value"
                placeholder="All sprints"
                :empty-label="sprintsLoadError || 'No sprints — add in System Setting → Sprint Setting'"
                @update:model-value="onSprintFilterChange"
              />
              <span
                v-if="activeView === 'sprint' && !sprintsLoading && sprintDropdownOptions.length <= 1"
                class="text-xs text-secondary mt-1 d-block"
              >
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="stats-row mb-4">
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-total">
              <i class="fas fa-tasks"></i>
            </div>
            <div>
              <p class="stat-label">Total</p>
              <h5 class="stat-value">{{ activeTasks.length }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-todo">
              <i class="fas fa-list-ul"></i>
            </div>
            <div>
              <p class="stat-label">To Do</p>
              <h5 class="stat-value">{{ todoCount }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-inprogress">
              <i class="fas fa-spinner"></i>
            </div>
            <div>
              <p class="stat-label">In Progress</p>
              <h5 class="stat-value">{{ inProgressCount }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-done">
              <i class="fas fa-check-circle"></i>
            </div>
            <div>
              <p class="stat-label">Done</p>
              <h5 class="stat-value">{{ doneCount }}</h5>
            </div>
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-card">
            <div class="stat-icon stat-icon-overdue">
              <i class="fas fa-clock"></i>
            </div>
            <div>
              <p class="stat-label">Overdue</p>
              <h5 class="stat-value">{{ overdueCount }}</h5>
            </div>
          </div>
        </div>
      </div>

      <div v-if="activeView === 'calendar'" class="mgr-calendar">
        <div class="mgr-calendar-nav mb-3">
          <button type="button" class="mgr-cal-nav-btn" aria-label="Previous month" @click="shiftCalendarMonth(-1)">
            <i class="fas fa-chevron-left"></i>
          </button>
          <h6 class="mb-0 mgr-calendar-title">{{ calendarMonthLabel }}</h6>
          <button type="button" class="mgr-cal-nav-btn" aria-label="Next month" @click="shiftCalendarMonth(1)">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
        <div class="mgr-calendar-grid">
          <div
            v-for="(label, idx) in calendarWeekdayLabels"
            :key="label"
            class="mgr-calendar-weekday"
            :class="{ 'mgr-calendar-weekday--off': idx === 0 || idx === 6 }"
          >
            {{ label }}
          </div>
          <div v-for="week in calendarWeeks" :key="week.key" class="mgr-calendar-week">
            <div
              v-for="day in week.days"
              :key="day.key"
              class="mgr-calendar-day"
              :class="{
                'mgr-calendar-day--muted': !day.inMonth,
                'mgr-calendar-day--today': day.isToday,
                'mgr-calendar-day--off': day.isOff,
              }"
              :style="{ minHeight: week.minHeight }"
            >
              <span class="mgr-calendar-num">{{ day.dayNum }}</span>
              <div v-if="day.isHoliday" class="mgr-calendar-holiday">
                <span class="mgr-calendar-holiday-tag">Holiday</span>
                <span class="mgr-calendar-holiday-name">{{ day.holidayName }}</span>
              </div>
            </div>
            <div class="mgr-calendar-week-bars" :style="week.barsStyle">
              <button
                v-for="bar in week.bars"
                :key="bar.key"
                type="button"
                class="mgr-calendar-event"
                :class="{
                  'mgr-calendar-event--cont-left': bar.continuesLeft,
                  'mgr-calendar-event--cont-right': bar.continuesRight,
                }"
                :style="{
                  gridColumn: `${bar.colStart} / span ${bar.span}`,
                  gridRow: bar.lane + 1,
                  borderLeftColor: bar.color,
                  background: bar.background,
                }"
                :title="bar.title"
                @click="openTaskDetail(bar.task)"
              >
                {{ bar.shortTitle }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="activeTasks.length === 0" class="text-center py-5">
        <i
          class="fa-3x text-secondary opacity-5"
          :class="activeView === 'sprint' ? 'fas fa-flag-checkered' : 'fas fa-users'"
        ></i>
        <p class="text-secondary text-sm mt-3 mb-0">
          {{
            activeView === "sprint"
              ? "No tasks for the selected team or sprint."
              : "No tasks for this team member."
          }}
        </p>
      </div>

      <!-- Tree table (Project / SubProject → tasks) -->
      <div v-else class="mgr-tree-table">
        <div class="mgr-tree-header">
          <span class="mgr-col-id">ID</span>
          <span class="mgr-col-name">TASK NAME</span>
          <span class="mgr-col-assignee text-center">ASSIGNEE</span>
          <span class="mgr-col-kind text-center">KIND</span>
          <span class="mgr-col-status text-center">STATE</span>
          <span class="mgr-col-due text-center">DUE DATE</span>
        </div>
        <template v-for="group in subProjectTree" :key="group.key">
          <div class="mgr-tree-group" @click="toggleSubProjectCollapse(group.key)">
            <i
              class="fas me-1 text-secondary mgr-tree-chevron"
              :class="isSubProjectCollapsed(group.key) ? 'fa-chevron-right' : 'fa-chevron-down'"
            ></i>
            <i
              class="fas me-2 text-warning"
              :class="isSubProjectCollapsed(group.key) ? 'fa-folder' : 'fa-folder-open'"
            ></i>
            <span class="text-xs font-weight-bold text-dark">{{ group.label }}</span>
            <span class="mgr-tree-meta ms-2">{{ group.tasks.length }} task(s)</span>
          </div>
          <template v-if="!isSubProjectCollapsed(group.key)">
            <div
              v-for="task in group.tasks"
              :key="taskKey(task)"
              class="mgr-tree-row mgr-tree-row-clickable"
              :class="{ 'mgr-tree-row-overdue': isOverdue(task) }"
              @click="openTaskDetail(task)"
            >
              <div class="mgr-col-id">
                <span class="text-xs text-secondary font-weight-bold">{{ taskRowId(task) }}</span>
              </div>
              <div class="mgr-col-name">
                <p class="mgr-task-name mb-0 mgr-task-name-link">{{ task.name }}</p>
                <div v-if="task.taskType === 'run' && task.total > 0" class="mgr-run-progress mt-1">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <span class="text-xs text-secondary">Progress ({{ task.total }} cases)</span>
                    <span class="text-xs font-weight-bold">{{ task.progress }}%</span>
                  </div>
                  <div class="task-progress-track">
                    <div class="task-progress-bar" :style="{ width: task.progress + '%' }"></div>
                  </div>
                </div>
              </div>
              <div class="mgr-col-assignee text-center">
                <div class="mgr-assignee-pills d-flex flex-wrap justify-content-center gap-1">
                  <span
                    v-for="(a, idx) in taskAssignees(task)"
                    :key="assigneeKey(a) + '-' + idx"
                    class="qa-badge assignee-badge bo-assignee-pill"
                  >{{ formatAssigneePill(a) }}</span>
                </div>
              </div>
              <div class="mgr-col-kind text-center">
                <span
                  class="task-type-badge"
                  :class="task.taskType === 'plan' ? 'badge-plan' : 'badge-run'"
                >
                  <i
                    :class="task.taskType === 'plan' ? planTypeIcon(task.planType) : 'fas fa-play-circle'"
                    class="me-1"
                  ></i>
                  {{ task.taskType === "plan" ? formatPlanType(task.planType) : "Test Run" }}
                </span>
              </div>
              <div class="mgr-col-status text-center">
                <span class="status-pill" :class="statusPillClass(task)">{{ statusLabel(task) }}</span>
              </div>
              <div class="mgr-col-due text-center mgr-col-due-stack">
                <span class="text-sm" :class="{ 'due-overdue': isOverdue(task) }">
                  {{ task.dueDate ? formatDate(task.dueDate) : "—" }}
                </span>
                <span v-if="isOverdue(task)" class="mgr-overdue-under">Overdue</span>
              </div>
            </div>
          </template>
        </template>
      </div>
      </div>
    </div>

    <!-- Task detail (plan = same as Plan Management; run = summary) -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="modal-overlay">
        <div class="modal-box modal-box-detail">
          <div class="d-flex align-items-center justify-content-between p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">Task Detail</h6>
          </div>
          <div v-if="detailLoading" class="modal-detail-body p-4 text-center text-secondary text-sm">
            Loading...
          </div>
          <div v-else-if="detailKind === 'plan' && detailItem" class="modal-detail-body p-4">
            <h6 class="font-weight-bold mb-3" style="font-size:1rem; color:#344767;">
              <span class="task-type-prefix">{{ ownerTypeLabel(taskOwnerType(detailItem)) }}</span>
              : {{ detailItem.testPlanName }}
            </h6>
            <div class="row g-3 mb-3">
              <div class="col-6">
                <p class="text-xs font-weight-bold text-secondary mb-1">START DATE</p>
                <p class="text-sm mb-0">{{ formatDetailDate(detailItem.planOverallStart) }}</p>
              </div>
              <div class="col-6">
                <p class="text-xs font-weight-bold text-secondary mb-1">DUE DATE</p>
                <p class="text-sm mb-0">{{ formatDetailDate(detailItem.planOverallDue) }}</p>
              </div>
            </div>
            <div class="mb-3">
              <p class="text-xs font-weight-bold text-secondary mb-1">OWNER TYPE</p>
              <p class="text-sm mb-0">{{ ownerTypeLabel(taskOwnerType(detailItem)) }}</p>
            </div>
            <div class="mb-3" v-if="detailItem.qaOwners && detailItem.qaOwners.length > 0">
              <p class="text-xs font-weight-bold text-secondary mb-1">OWNER TASK</p>
              <div class="d-flex flex-wrap gap-1">
                <span
                  v-for="owner in detailItem.qaOwners"
                  :key="owner.userId"
                  class="qa-badge assignee-badge bo-assignee-pill"
                >{{ formatAssignedName(owner.fullName) }}</span>
              </div>
            </div>
            <div class="mb-3">
              <p class="text-xs font-weight-bold text-secondary mb-1">DETAILS</p>
              <p class="text-sm mb-0 detail-text-block">{{ detailItem.details || "—" }}</p>
            </div>
            <div class="modal-detail-remarks">
              <p class="text-xs font-weight-bold text-secondary mb-2">REMARK</p>
              <div v-if="detailActivityLoading" class="text-secondary text-xs py-2">Loading...</div>
              <div v-else-if="detailRemarks.length === 0" class="text-secondary text-xs py-2">No remarks yet.</div>
              <div v-else class="detail-remark-list">
                <div
                  v-for="entry in detailRemarks"
                  :key="entry.activityId"
                  class="activity-history-item"
                >
                  <div class="d-flex justify-content-between gap-2 mb-1">
                    <span class="activity-badge badge-remark">Remark</span>
                    <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
                  </div>
                  <p class="text-xs text-secondary mb-1">{{ entry.authorName }}</p>
                  <p class="text-sm mb-0 activity-text"><MentionText :text="entry.remarkText" /></p>
                </div>
              </div>
            </div>
          </div>
          <div v-else-if="detailKind === 'run' && detailItem" class="modal-detail-body p-4">
            <h6 class="font-weight-bold mb-3" style="font-size:1rem; color:#344767;">
              {{ detailItem.name }}
            </h6>
            <div class="mb-3">
              <p class="text-xs font-weight-bold text-secondary mb-1">PROJECT / SUB PROJECT</p>
              <p class="text-sm mb-0">{{ detailItem.projectName || "—" }} / {{ detailItem.subProjectName || "—" }}</p>
            </div>
            <div class="mb-3">
              <p class="text-xs font-weight-bold text-secondary mb-1">ASSIGNEE</p>
              <div class="d-flex flex-wrap gap-1">
                <span
                  v-for="(a, idx) in taskAssignees(detailItem)"
                  :key="assigneeKey(a) + '-' + idx"
                  class="qa-badge assignee-badge bo-assignee-pill"
                >{{ formatAssigneePill(a) }}</span>
              </div>
            </div>
            <div class="row g-3 mb-3">
              <div class="col-6">
                <p class="text-xs font-weight-bold text-secondary mb-1">STATE</p>
                <p class="text-sm mb-0">{{ statusLabel(detailItem) }}</p>
              </div>
              <div class="col-6">
                <p class="text-xs font-weight-bold text-secondary mb-1">DUE DATE</p>
                <p class="text-sm mb-0">{{ formatDetailDate(detailItem.dueDate) }}</p>
              </div>
            </div>
            <div v-if="detailItem.total > 0" class="mb-0">
              <p class="text-xs font-weight-bold text-secondary mb-1">PROGRESS</p>
              <p class="text-sm mb-2">{{ detailItem.progress }}% ({{ detailItem.total }} test cases)</p>
              <div class="task-progress-track">
                <div class="task-progress-bar" :style="{ width: detailItem.progress + '%' }"></div>
              </div>
            </div>
          </div>
          <div class="p-3 border-top d-flex justify-content-end">
            <button type="button" class="btn btn-sm btn-outline-secondary btn-modal-close mb-0" @click="closeDetailModal">Close</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { sprintSettingsApi, testPlanItemsApi, teamTasksApi } from "@/services/backendApi";
import MentionText from "@/components/MentionText.vue";
import {
  parseUserTeams,
  teamsOverlap,
  userHasAllTeams,
  userHasMultipleTeams,
  teamCodesForPersonFilter,
} from "@/utils/userTeams";
import {
  statusLabel as workflowStatusLabel,
  statusPillClass as workflowStatusPillClass,
  isWorkflowStatusKey,
} from "@/utils/subProjectStatus";
import { getThaiHolidayName, isThaiPublicHoliday } from "@/utils/thaiHolidays";

export default {
  name: "Management",
  components: { MentionText },
  data() {
    return {
      loading: true,
      loadError: null,
      teamTasksRequestId: 0,
      teamTasksLoaded: false,
      members: [],
      tasks: [],
      sprints: [],
      selectedMemberId: "all",
      selectedPersonTeam: "a",
      selectedSprintId: "all",
      selectedSprintTeam: "all",
      sprintsLoading: false,
      sprintsLoadError: null,
      activeView: "sprint",
      calendarScope: "team",
      calendarCursor: new Date(),
      calendarWeekdayLabels: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      collapsedSubProjects: [],
      showDetailModal: false,
      detailItem: null,
      detailKind: null,
      detailLoading: false,
      detailActivityHistory: [],
      detailActivityLoading: false,
    };
  },
  computed: {
    detailRemarks() {
      return (this.detailActivityHistory || []).filter((e) => e.entryType === "remark");
    },
    ...mapState(["user"]),
    resolvedManagerId() {
      const u = this.user;
      if (!u) return null;
      const raw = u.userId ?? u.user_id ?? u.id;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    },
    sprintOptions() {
      // Show every active sprint across teams; sorted by start date desc.
      return [...this.sprints].sort((a, b) => {
        const ad = String(a.startDate || "");
        const bd = String(b.startDate || "");
        return bd.localeCompare(ad);
      });
    },
    normalizedUserTeam() {
      return this.user?.teams?.length ? this.user.teams : parseUserTeams(this.user?.team);
    },
    hasMultiplePersonTeams() {
      return userHasMultipleTeams(this.user?.team || this.user?.teams);
    },
    personTeamLocked() {
      return !this.hasMultiplePersonTeams;
    },
    effectivePersonTeam() {
      if (this.personTeamLocked) {
        const codes = teamCodesForPersonFilter(this.user?.team || this.user?.teams);
        return codes[0] || "a";
      }
      return this.selectedPersonTeam;
    },
    personTeamOptions() {
      const labels = { a: "Team A", b: "Team B", c: "Team C" };
      const codes = teamCodesForPersonFilter(this.user?.team || this.user?.teams);
      return codes.map((key) => ({ value: key, label: labels[key] || key }));
    },
    membersForPersonTeam() {
      const team = this.effectivePersonTeam;
      return this.members.filter((m) => {
        if (userHasAllTeams(m.team)) return true;
        return teamsOverlap(m.team, team);
      });
    },
    isPersonScopedView() {
      return this.activeView === "person" || (this.activeView === "calendar" && this.calendarScope === "person");
    },
    showTeamFilter() {
      return this.activeView === "sprint" || (this.activeView === "calendar" && this.calendarScope === "team");
    },
    showPersonFilters() {
      return this.isPersonScopedView;
    },
    memberDropdownOptions() {
      const members = this.isPersonScopedView ? this.membersForPersonTeam : this.members;
      const opts = members.map((m) => ({
        value: m.userId,
        label: m.name,
      }));
      if (this.isPersonScopedView) {
        return [{ value: "all", label: "All members" }, ...opts];
      }
      return opts;
    },
    teamDropdownOptions() {
      return [
        { value: "all", label: "All teams" },
        { value: "a", label: "Team A" },
        { value: "b", label: "Team B" },
        { value: "c", label: "Team C" },
      ];
    },
    sprintDropdownOptions() {
      const teamKey = this.isPersonScopedView
        ? this.effectivePersonTeam
        : this.selectedSprintTeam;
      const list = this.sprintsForTeam(teamKey);
      const items = list
        .map((s) => {
          const id = Number(s.sprintId ?? s.sprintSettingId);
          if (!Number.isFinite(id)) return null;
          return { value: id, label: this.sprintDropdownLabel(s) };
        })
        .filter(Boolean);
      return [{ value: "all", label: "All sprints" }, ...items];
    },
    /** Tasks shown in the active view (person = member + sprint; sprint = team + sprint). */
    activeTasks() {
      let result = this.tasks;
      if (this.isPersonScopedView) {
        result = this.filterTasksBySprintTeam(result, this.effectivePersonTeam);
        if (this.selectedMemberId !== "all" && this.selectedMemberId != null) {
          result = result.filter(
            (t) => Number(t.assigneeId) === Number(this.selectedMemberId)
          );
        }
      } else if (this.showTeamFilter && this.selectedSprintTeam !== "all") {
        result = this.filterTasksBySprintTeam(result, this.selectedSprintTeam);
      }
      return this.filterTasksBySprintId(result);
    },
    calendarMonthLabel() {
      return this.calendarCursor.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    },
    calendarItems() {
      const map = new Map();
      for (const task of this.activeTasks) {
        const key = this.taskKey(task);
        if (!map.has(key)) {
          map.set(key, {
            ...task,
            assignees: [this.assigneeEntryFromTask(task)],
          });
        } else {
          const row = map.get(key);
          map.set(key, {
            ...row,
            assignees: this.mergeAssigneeList(row.assignees, task),
          });
        }
      }
      return [...map.values()];
    },
    calendarWeeks() {
      const year = this.calendarCursor.getFullYear();
      const month = this.calendarCursor.getMonth();
      const first = new Date(year, month, 1);
      const gridStart = new Date(first);
      gridStart.setDate(gridStart.getDate() - gridStart.getDay());
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayKey = this.dateKey(today);
      const items = this.calendarItems
        .map((task) => {
          const startDate = this.parseDateOnly(task.startDate);
          const dueDate = this.parseDateOnly(task.dueDate);
          const rangeStart = startDate || dueDate;
          const rangeEnd = dueDate || startDate;
          if (!rangeStart || !rangeEnd) return null;
          const s = rangeStart <= rangeEnd ? rangeStart : rangeEnd;
          const e = rangeEnd >= rangeStart ? rangeEnd : rangeStart;
          const name = String(task.name || "Untitled").trim() || "Untitled";
          const color = this.calendarEventColor(task);
          return {
            task,
            rangeStart: s,
            rangeEnd: e,
            title: `${name} · ${this.statusLabel(task)}`,
            shortTitle: name,
            color,
            background: `${color}22`,
          };
        })
        .filter(Boolean)
        .sort((a, b) => {
          const startCmp = a.rangeStart - b.rangeStart;
          if (startCmp !== 0) return startCmp;
          return b.rangeEnd - a.rangeEnd;
        });

      const weeks = [];
      for (let w = 0; w < 6; w += 1) {
        const weekStart = new Date(gridStart);
        weekStart.setDate(gridStart.getDate() + w * 7);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        const days = [];
        for (let i = 0; i < 7; i += 1) {
          const d = new Date(weekStart);
          d.setDate(weekStart.getDate() + i);
          d.setHours(0, 0, 0, 0);
          const holidayName = getThaiHolidayName(d);
          const isHoliday = Boolean(holidayName) || isThaiPublicHoliday(d);
          const isWeekend = d.getDay() === 0 || d.getDay() === 6;
          days.push({
            key: this.dateKey(d),
            date: d,
            dayNum: d.getDate(),
            inMonth: d.getMonth() === month,
            isToday: this.dateKey(d) === todayKey,
            isWeekend,
            isHoliday,
            isOff: isWeekend || isHoliday,
            holidayName,
          });
        }
        const rawBars = [];
        items
          .filter((item) => item.rangeStart <= weekEnd && item.rangeEnd >= weekStart)
          .forEach((item) => {
            let seg = null;
            const flush = () => {
              if (!seg) return;
              const prevDay = this.addDays(days[seg.colStart - 1].date, -1);
              const nextDay = this.addDays(days[seg.colEnd - 1].date, 1);
              rawBars.push({
                key: `${this.dateKey(weekStart)}-${this.taskKey(item.task)}-${seg.colStart}`,
                task: item.task,
                title: item.title,
                shortTitle: item.shortTitle,
                color: item.color,
                background: item.background,
                colStart: seg.colStart,
                span: Math.max(1, seg.colEnd - seg.colStart + 1),
                continuesLeft:
                  item.rangeStart <= prevDay && this.isWorkingDay(prevDay),
                continuesRight:
                  item.rangeEnd >= nextDay && this.isWorkingDay(nextDay),
              });
              seg = null;
            };
            days.forEach((day, idx) => {
              const col = idx + 1;
              const inRange = day.date >= item.rangeStart && day.date <= item.rangeEnd;
              if (inRange && !day.isOff) {
                if (seg && seg.colEnd === col - 1) seg.colEnd = col;
                else {
                  flush();
                  seg = { colStart: col, colEnd: col };
                }
              } else {
                flush();
              }
            });
            flush();
          });
        const lanes = [];
        const bars = rawBars.map((bar) => {
          const barStart = bar.colStart;
          const barEnd = bar.colStart + bar.span - 1;
          let lane = lanes.findIndex((laneRanges) =>
            laneRanges.every((r) => barEnd < r.start || barStart > r.end)
          );
          if (lane < 0) {
            lane = lanes.length;
            lanes.push([]);
          }
          lanes[lane].push({ start: barStart, end: barEnd });
          return { ...bar, lane };
        });
        const laneCount = Math.max(lanes.length, 1);
        weeks.push({
          key: this.dateKey(weekStart),
          days,
          bars,
          minHeight: `${Math.max(6.2, 1.7 + laneCount * 1.28)}rem`,
          barsStyle: {
            gridTemplateRows: `repeat(${laneCount}, 1.12rem)`,
          },
        });
      }
      return weeks;
    },
    subProjectTree() {
      return this.buildSubProjectTree(this.activeTasks);
    },
    overdueCount() {
      return this.activeTasks.filter((t) => this.isOverdue(t)).length;
    },
    todoCount() {
      return this.activeTasks.filter((t) => this.taskStatusKey(t) === "todo").length;
    },
    inProgressCount() {
      return this.activeTasks.filter((t) => this.taskStatusKey(t) === "inprogress").length;
    },
    doneCount() {
      return this.activeTasks.filter((t) => this.taskStatusKey(t) === "done").length;
    },
  },

  watch: {
    activeView(view) {
      if (!this.sprints.length && !this.sprintsLoading) {
        this.loadSprints();
      }
      if (view === "person" || (view === "calendar" && this.calendarScope === "person")) {
        this.initPersonTeamFromUser();
        this.ensureMemberSelectionValid();
      }
    },
    calendarScope(scope) {
      if (this.activeView !== "calendar") return;
      if (scope === "person") {
        this.initPersonTeamFromUser();
        this.ensureMemberSelectionValid();
      }
    },
    resolvedManagerId: {
      immediate: true,
      handler(id, prevId) {
        this.initPersonTeamFromUser();
        if (!id) {
          this.loading = false;
          this.loadError = this.user
            ? "Cannot read your user id. Please sign out and sign in again."
            : null;
          this.members = [];
          this.tasks = [];
          this.teamTasksLoaded = false;
          return;
        }
        if (id === prevId && this.teamTasksLoaded) return;
        this.loadTeamTasks();
      },
    },
  },
  async created() {
    this.initPersonTeamFromUser();
    await this.loadSprints();
  },
  methods: {
    onSprintFilterChange(value) {
      this.selectedSprintId = value;
    },
    shiftCalendarMonth(delta) {
      const d = new Date(this.calendarCursor);
      d.setMonth(d.getMonth() + delta);
      this.calendarCursor = d;
    },
    parseDateOnly(val) {
      if (!val) return null;
      const raw = String(val).slice(0, 10);
      const [y, m, d] = raw.split("-").map(Number);
      if (!y || !m || !d) return null;
      return new Date(y, m - 1, d);
    },
    dateKey(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    daysBetween(from, to) {
      const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
      const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
      return Math.round((b - a) / 86400000);
    },
    addDays(date, days) {
      const d = new Date(date);
      d.setDate(d.getDate() + days);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    isWorkingDay(date) {
      if (!date) return false;
      const dow = date.getDay();
      if (dow === 0 || dow === 6) return false;
      return !isThaiPublicHoliday(date);
    },
    calendarEventColor(task) {
      if (this.isOverdue(task)) return "#f5365c";
      const key = this.taskStatusKey(task);
      if (key === "done") return "#2dce89";
      if (key === "inprogress") return "#5e72e4";
      if (key === "todo") return "#8392ab";
      if (String(task.status || "").toLowerCase().includes("fail")) return "#f5365c";
      return task.taskType === "run" ? "#11cdef" : "#2152ff";
    },
    sprintsForTeam(teamKey) {
      if (teamKey === "all") return this.sprintOptions;
      return this.sprintOptions.filter(
        (s) => String(s.team || "").trim().toLowerCase() === teamKey
      );
    },
    sprintIdsForTeam(teamKey) {
      return new Set(
        this.sprintsForTeam(teamKey)
          .map((s) => Number(s.sprintId ?? s.sprintSettingId))
          .filter(Number.isFinite)
      );
    },
    taskRowId(task) {
      const prefix = task.taskType === "run" ? "RUN" : "TSK";
      return `${prefix}-${task.id}`;
    },
    assigneeEntryFromTask(source) {
      return {
        userId: source.assigneeId,
        firstName: source.assigneeFirstName,
        lastName: source.assigneeLastName,
        nickname: source.assigneeNickname,
        legacyName: source.assigneeName,
      };
    },
    mergeAssigneeList(existing, source) {
      const entry = this.assigneeEntryFromTask(source);
      const key = this.assigneeKey(entry);
      if (existing.some((a) => this.assigneeKey(a) === key)) return existing;
      return [...existing, entry];
    },
    buildSubProjectTree(tasks) {
      const groups = new Map();
      for (const task of tasks) {
        const subName = String(task.subProjectName || "").trim() || "— No sub project";
        const projectName = String(task.projectName || "").trim() || "—";
        const groupKey = `${projectName}|${subName}`;
        if (!groups.has(groupKey)) {
          groups.set(groupKey, {
            key: groupKey,
            projectName,
            subProjectName: subName,
            tasks: [],
          });
        }
        const g = groups.get(groupKey);
        const mergeKey = `${task.taskType}-${task.id}`;
        const idx = g.tasks.findIndex((t) => `${t.taskType}-${t.id}` === mergeKey);
        if (idx < 0) {
          g.tasks.push({
            ...task,
            assignees: [this.assigneeEntryFromTask(task)],
          });
        } else {
          const row = g.tasks[idx];
          g.tasks[idx] = {
            ...row,
            assignees: this.mergeAssigneeList(row.assignees, task),
          };
        }
      }
      return [...groups.values()]
        .map((g) => ({
          ...g,
          label: `${g.projectName} / ${g.subProjectName}`,
          tasks: this.sortTasksByIdAsc(g.tasks),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
    },
    assigneeKey(a) {
      if (a.userId != null && a.userId !== "") return `id:${a.userId}`;
      return `name:${String(a.legacyName || a.firstName || "").trim()}`;
    },
    taskAssignees(task) {
      if (Array.isArray(task.assignees) && task.assignees.length) return task.assignees;
      return [
        {
          userId: task.assigneeId,
          firstName: task.assigneeFirstName,
          lastName: task.assigneeLastName,
          nickname: task.assigneeNickname,
          legacyName: task.assigneeName,
        },
      ];
    },
    formatAssigneePill(assignee) {
      const first = String(assignee?.firstName || "").trim();
      const nick = String(assignee?.nickname || "").trim();
      if (first) return nick ? `${first} (${nick})` : first;
      const legacy = String(assignee?.legacyName || "").trim();
      if (legacy) return this.formatAssignedName(legacy);
      return "—";
    },
    formatAssignedName(rawName) {
      const name = String(rawName || "").trim();
      if (!name) return "—";
      const nicknameMatch = name.match(/\(([^)]+)\)/);
      const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
      if (nicknameMatch && nicknameMatch[1]) {
        return `${firstName} (${nicknameMatch[1].trim()})`;
      }
      return firstName || name;
    },
    async openTaskDetail(task) {
      if (task.taskType === "plan") {
        this.detailKind = "plan";
        this.detailLoading = true;
        this.showDetailModal = true;
        this.detailItem = null;
        this.detailActivityHistory = [];
        try {
          this.detailItem = await testPlanItemsApi.getById(task.id);
          await this.loadDetailActivityHistory();
        } catch (e) {
          console.error("openTaskDetail error:", e);
          alert(e.message || "Failed to load task detail");
          this.closeDetailModal();
        } finally {
          this.detailLoading = false;
        }
      } else {
        this.detailKind = "run";
        this.detailItem = { ...task, assignees: this.taskAssignees(task) };
        this.detailActivityHistory = [];
        this.showDetailModal = true;
      }
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.detailItem = null;
      this.detailKind = null;
      this.detailActivityHistory = [];
      this.detailActivityLoading = false;
      this.detailLoading = false;
    },
    async loadDetailActivityHistory() {
      if (!this.detailItem?.testPlanId) return;
      this.detailActivityLoading = true;
      try {
        this.detailActivityHistory = await testPlanItemsApi.getActivity(
          this.detailItem.testPlanId
        );
      } catch (e) {
        console.error("loadDetailActivityHistory error:", e);
        this.detailActivityHistory = [];
      } finally {
        this.detailActivityLoading = false;
      }
    },
    taskOwnerType(item) {
      if (!item) return null;
      const raw = item.ownerType != null ? item.ownerType : item.owner_type;
      const s = raw != null ? String(raw).trim() : "";
      return s || null;
    },
    ownerTypeLabel(val) {
      const raw = val != null ? String(val).trim() : "";
      if (!raw) return "—";
      const map = { qa: "QA", dev: "DEV", pm: "PM", dev_ops: "DEV OPS" };
      return map[raw.toLowerCase()] || raw;
    },
    formatDetailDate(val) {
      if (!val) return "—";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      if (!y || !m || !d) return "—";
      return `${d}/${m}/${y}`;
    },
    formatActivityTime(val) {
      if (!val) return "";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    isSubProjectCollapsed(groupKey) {
      return this.collapsedSubProjects.includes(groupKey);
    },
    toggleSubProjectCollapse(groupKey) {
      const idx = this.collapsedSubProjects.indexOf(groupKey);
      if (idx >= 0) this.collapsedSubProjects.splice(idx, 1);
      else this.collapsedSubProjects.push(groupKey);
    },
    sortTasksByIdAsc(tasks) {
      return [...tasks].sort((a, b) => {
        const idCmp = Number(a.id) - Number(b.id);
        if (idCmp !== 0) return idCmp;
        const typeCmp = String(a.taskType || "").localeCompare(String(b.taskType || ""));
        if (typeCmp !== 0) return typeCmp;
        return String(a.name || "")
          .trim()
          .localeCompare(String(b.name || "").trim(), undefined, { sensitivity: "base" });
      });
    },
    initPersonTeamFromUser() {
      const t = this.normalizedUserTeam;
      if (t === "all") {
        if (!["a", "b", "c"].includes(this.selectedPersonTeam)) {
          this.selectedPersonTeam = "a";
        }
      } else if (t === "a" || t === "b" || t === "c") {
        this.selectedPersonTeam = t;
      }
    },
    switchPersonTeam(team) {
      if (this.personTeamLocked || this.effectivePersonTeam === team) return;
      this.selectedPersonTeam = team;
      this.resetSprintIfNotInTeam(team);
      this.ensureMemberSelectionValid();
    },
    switchSprintTeam(team) {
      if (this.selectedSprintTeam === team) return;
      this.selectedSprintTeam = team;
      this.resetSprintIfNotInTeam(team);
    },
    resetSprintIfNotInTeam(team) {
      const nextSprintIds = this.sprintIdsForTeam(team);
      if (
        this.selectedSprintId !== "all" &&
        this.selectedSprintId != null &&
        !nextSprintIds.has(Number(this.selectedSprintId))
      ) {
        this.selectedSprintId = "all";
      }
    },
    filterTasksBySprintTeam(tasks, teamKey) {
      if (!teamKey || teamKey === "all") return tasks;
      const allowed = this.sprintIdsForTeam(teamKey);
      return tasks.filter((t) => {
        const sid =
          t.sprintId != null && t.sprintId !== ""
            ? Number(t.sprintId)
            : null;
        if (sid == null) return teamKey === "c";
        return allowed.has(sid);
      });
    },
    filterTasksBySprintId(tasks) {
      if (this.selectedSprintId === "all" || this.selectedSprintId == null) {
        return tasks;
      }
      return tasks.filter(
        (t) => Number(t.sprintId) === Number(this.selectedSprintId)
      );
    },
    ensureMemberSelectionValid() {
      if (this.selectedMemberId === "all" || this.selectedMemberId == null) return;
      const allowed = new Set(
        this.membersForPersonTeam.map((m) => Number(m.userId))
      );
      if (!allowed.has(Number(this.selectedMemberId))) {
        this.selectedMemberId = "all";
      }
    },
    taskKey(task) {
      return `${task.taskType}-${task.id}`;
    },
    async loadSprints() {
      this.sprintsLoading = true;
      this.sprintsLoadError = null;
      try {
        const raw = await sprintSettingsApi.getAll();
        this.sprints = Array.isArray(raw) ? raw : [];
      } catch (e) {
        console.error("loadSprints error:", e.message || e);
        this.sprints = [];
        this.sprintsLoadError = e.message || "Failed to load sprints";
      } finally {
        this.sprintsLoading = false;
      }
    },
    sprintDropdownLabel(s) {
      const round =
        s.sprintRound != null && s.sprintRound !== ""
          ? `Sprint ${s.sprintRound}`
          : "Sprint";
      const name = s.sprintName ? ` — ${s.sprintName}` : "";
      const start = this.formatSprintDate(s.startDate);
      const end = this.formatSprintDate(s.endDate);
      const range = start && end ? ` (${start} – ${end})` : "";
      return `${round}${name}${range}`;
    },
    formatSprintDate(val) {
      if (!val) return "";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      if (!y || !m || !d) return "";
      return `${d}/${m}/${y}`;
    },
    onSprintChange(event) {
      const raw = event.target.value;
      this.selectedSprintId = raw === "" ? null : Number(raw);
    },
    async loadTeamTasks() {
      const userId = this.resolvedManagerId;
      if (!userId) {
        this.loading = false;
        this.loadError = null;
        this.teamTasksLoaded = false;
        return;
      }
      const requestId = ++this.teamTasksRequestId;
      this.loading = true;
      this.loadError = null;
      try {
        const data = await teamTasksApi.getByUser(userId);
        if (requestId !== this.teamTasksRequestId) return;
        this.members = data?.members || [];
        this.tasks = data?.tasks || [];
        this.teamTasksLoaded = true;
      } catch (e) {
        if (requestId !== this.teamTasksRequestId) return;
        console.error("loadTeamTasks error:", e.message || e);
        this.loadError = e.message || "Failed to load team tasks";
        this.members = [];
        this.tasks = [];
        this.teamTasksLoaded = false;
      } finally {
        if (requestId === this.teamTasksRequestId) {
          this.loading = false;
        }
      }
    },
    isOverdue(task) {
      if (!task?.dueDate) return false;
      if (this.taskStatusKey(task) === "done") return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const due = new Date(task.dueDate);
      due.setHours(0, 0, 0, 0);
      return due < today;
    },
    formatDate(d) {
      if (!d) return "";
      const [y, m, day] = d.split("-");
      return `${day}/${m}/${y}`;
    },
    statusLabel(task) {
      if (task.taskType === "plan" && isWorkflowStatusKey(task.status)) {
        return workflowStatusLabel(task.status);
      }
      if (task.taskType === "plan") {
        const s = String(task.status || "").replace(/_/g, " ");
        return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
      }
      return task.status || "—";
    },
    statusPillClass(task) {
      if (task.taskType === "plan" && isWorkflowStatusKey(task.status)) {
        return workflowStatusPillClass(task.status);
      }
      if (task.taskType !== "plan") {
        const map = {
          "Not Started": "pill-not-started",
          "In Progress": "pill-run-inprogress",
          Done: "pill-run-done",
          Failed: "pill-run-failed",
        };
        return map[task.status] || "pill-draft";
      }
      return workflowStatusPillClass(task.status);
    },
    onMemberChange(event) {
      const raw = event.target.value;
      this.selectedMemberId = raw === "" ? "all" : raw === "all" ? "all" : Number(raw);
    },
    taskStatusKey(task) {
      const raw = String(task.status || "").toLowerCase().trim();
      if (task.taskType === "plan") {
        if (raw === "to_do" || raw === "todo") return "todo";
        if (raw === "in_progress" || raw === "inprogress") return "inprogress";
        return raw;
      }
      if (raw === "not started") return "todo";
      if (raw === "in progress") return "inprogress";
      if (raw === "done") return "done";
      return raw;
    },
    formatPlanType(planType) {
      return String(planType || "plan").replace(/_/g, " ");
    },
    planTypeIcon(planType) {
      const key = String(planType || "plan").toLowerCase();
      const map = {
        plan: "fas fa-calendar-check",
        support: "fas fa-cog",
        other: "fas fa-tasks",
      };
      return map[key] || "fas fa-tag";
    },
  },
};
</script>

<style scoped>
.page-title {
  color: #344767;
}
.mgr-shell-card {
  border-radius: 0.75rem;
}
.mgr-shell-card .card-header {
  padding-top: 1rem !important;
  padding-bottom: 1rem !important;
}
.mgr-shell-content {
  padding-top: 1rem;
}
.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  border-radius: 12px;
  background: transparent;
}
.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}
.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}
.mgr-scope-toggle {
  display: flex;
  gap: 0.3rem;
  background: #f5f5f5;
  border-radius: 10px;
  padding: 0.2rem;
}
.mgr-scope-btn {
  flex: 1;
  border: none;
  background: transparent;
  color: #67748e;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  border-radius: 8px;
  padding: 0.4rem 0.6rem;
  cursor: pointer;
}
.mgr-scope-btn.active {
  background: #344767;
  color: #fff;
}
.mgr-calendar-nav {
  display: grid;
  grid-template-columns: 2rem 1fr 2rem;
  align-items: center;
  gap: 0.5rem;
}
.mgr-calendar-title {
  text-align: center;
  font-weight: 700;
  color: #344767;
}
.mgr-cal-nav-btn {
  width: 2rem;
  height: 2rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  color: #67748e;
  cursor: pointer;
}
.mgr-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}
.mgr-calendar-weekday {
  padding: 0.5rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-align: center;
  color: #8392ab;
  background: #fafbfc;
  border-bottom: 1px solid #e9ecef;
}
.mgr-calendar-weekday--off {
  background: #eceff3;
  color: #adb5bd;
}
.mgr-calendar-week {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  position: relative;
  border-bottom: 1px solid #e9ecef;
}
.mgr-calendar-week:last-child {
  border-bottom: none;
}
.mgr-calendar-day {
  min-height: 6.2rem;
  padding: 0.35rem;
  border-right: 1px solid #e9ecef;
  background: #fff;
}
.mgr-calendar-day:nth-child(7n) {
  border-right: none;
}
.mgr-calendar-day--muted {
  background: #f8f9fa;
}
.mgr-calendar-day--muted .mgr-calendar-num {
  color: #adb5bd;
}
.mgr-calendar-day--off {
  background: #e9edf2;
}
.mgr-calendar-day--off .mgr-calendar-num {
  color: #8b95a5;
}
.mgr-calendar-holiday {
  margin-top: 0.35rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}
.mgr-calendar-holiday-tag {
  font-size: 0.58rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #67748e;
}
.mgr-calendar-holiday-name {
  font-size: 0.58rem;
  font-weight: 600;
  color: #8392ab;
  line-height: 1.25;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}
.mgr-calendar-day--today .mgr-calendar-num {
  background: #344767;
  color: #fff;
  border-radius: 999px;
  width: 1.35rem;
  height: 1.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.mgr-calendar-num {
  font-size: 0.75rem;
  font-weight: 700;
  color: #344767;
}
.mgr-calendar-week-bars {
  position: absolute;
  left: 0;
  right: 0;
  top: 1.55rem;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  column-gap: 0.12rem;
  row-gap: 0.14rem;
  padding: 0 0.1rem;
  pointer-events: none;
}
.mgr-calendar-event {
  display: block;
  min-width: 0;
  text-align: left;
  font-size: 0.62rem;
  font-weight: 600;
  color: #344767;
  background: #f4f6f9;
  border: none;
  border-left: 3px solid #2152ff;
  border-radius: 5px;
  padding: 0.12rem 0.35rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  pointer-events: auto;
}
.mgr-calendar-event--cont-left {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-left-width: 0;
  margin-left: -0.1rem;
}
.mgr-calendar-event--cont-right {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin-right: -0.1rem;
}
.team-card {
  background: linear-gradient(135deg, #f8faff 0%, #eef2ff 100%);
  border-color: #d6dcf5;
}
/* Tree table (BY SPRINT) — aligned with Test Case tab style */
.mgr-tree-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}
.mgr-tree-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem 0.65rem 2.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.mgr-tree-group {
  display: flex;
  align-items: center;
  padding: 0.55rem 1rem;
  background: #f3f4f6;
  border-bottom: 1px solid #e9ecef;
  border-top: 1px solid #e2e5e9;
  cursor: pointer;
}
.mgr-tree-group:first-of-type {
  border-top: none;
}
.mgr-tree-chevron {
  font-size: 0.65rem;
  width: 12px;
}
.mgr-tree-meta {
  font-size: 0.68rem;
  font-weight: 600;
  color: #8392ab;
}
.mgr-tree-row {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}
.mgr-tree-row:hover {
  background: #f5f8ff;
}
.mgr-tree-row-clickable {
  cursor: pointer;
}
.mgr-task-name-link {
  color: #344767;
}
.mgr-assignee-pills {
  min-height: 1.25rem;
}
.qa-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.55rem;
}
.assignee-badge {
  font-size: 0.7rem !important;
  padding: 0.04rem 0.26rem !important;
  line-height: 1.05;
  font-weight: 500;
}
.bo-assignee-pill {
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border-color: #bee5eb !important;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  width: 480px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
}
.modal-box-detail {
  width: 560px;
  max-width: 96vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.modal-detail-body {
  overflow-y: auto;
  max-height: calc(90vh - 110px);
}
.detail-text-block {
  white-space: pre-wrap;
  word-break: break-word;
  color: #344767;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  min-height: 2.5rem;
}
.modal-detail-remarks {
  border-top: 1px solid #e9ecef;
  padding-top: 0.75rem;
}
.detail-remark-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 2px;
}
.activity-history-item {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
}
.activity-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}
.badge-remark {
  background: #e8f4fd;
  color: #0c7a9a;
}
.activity-text {
  white-space: pre-wrap;
  word-break: break-word;
  color: #344767;
}
.btn-modal-close {
  color: #b22222 !important;
  border-color: #b22222 !important;
  background-color: transparent !important;
}
.btn-modal-close:hover,
.btn-modal-close:focus {
  color: #b22222 !important;
  background-color: #e1c5c5 !important;
  border-color: #b22222 !important;
}
.task-type-prefix {
  font-weight: 700;
  color: #8392ab;
}
.mgr-tree-row-overdue {
  background: #fffafa;
}
.mgr-col-id {
  flex: 0.6;
  min-width: 56px;
}
.mgr-col-name {
  flex: 3.2;
  min-width: 0;
}
.mgr-col-assignee {
  flex: 1.3;
  min-width: 100px;
}
.mgr-col-kind {
  flex: 1.2;
  min-width: 90px;
}
.mgr-col-status {
  flex: 1.4;
  min-width: 110px;
}
.mgr-col-due {
  flex: 1;
  min-width: 88px;
}
.mgr-tree-header .mgr-col-assignee,
.mgr-tree-header .mgr-col-kind,
.mgr-tree-header .mgr-col-status,
.mgr-tree-header .mgr-col-due,
.mgr-tree-row .mgr-col-assignee,
.mgr-tree-row .mgr-col-kind,
.mgr-tree-row .mgr-col-status,
.mgr-tree-row .mgr-col-due {
  display: flex;
  align-items: center;
  justify-content: center;
}
.mgr-col-due-stack {
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
}
.mgr-assignee-name {
  font-size: 0.8rem;
  font-weight: 600;
  color: #344767;
}
.mgr-overdue-under {
  font-size: 0.65rem;
  font-weight: 700;
  color: #e74c3c;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.mgr-task-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #344767;
}
.mgr-task-sub {
  font-size: 0.78rem;
  color: #8392ab;
  margin-top: 2px;
}
.mgr-run-progress {
  max-width: 280px;
}
.mgr-filters-row,
.stats-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.6rem;
  /*
   * NOTE: do NOT use overflow-x: auto here. Filter cards contain
   * <base-select> popups that are absolutely positioned beneath the
   * trigger; overflow on the parent clips the popup.
   */
}
.stat-col {
  flex: 1 1 0;
  min-width: 110px;
}
.mgr-filters-row .stat-col-filter {
  flex: 2 1 0;
  min-width: 200px;
}
@media (max-width: 1200px) {
  .mgr-filters-row,
  .stats-row {
    flex-wrap: wrap;
  }
  .stat-col {
    flex: 1 1 160px;
  }
  .mgr-filters-row .stat-col-filter {
    flex: 1 1 240px;
  }
}
.filter-card {
  background: linear-gradient(135deg, #f8faff 0%, #eef2ff 100%);
  border-color: #d6dcf5;
}
.filter-content {
  flex: 1;
  min-width: 0;
}
.member-filter-select {
  font-size: 0.85rem;
  font-weight: 600;
  border: none;
  background: transparent;
  padding: 0.1rem 1.4rem 0.1rem 0;
  color: var(--text-primary);
  box-shadow: none;
  height: auto;
  cursor: pointer;
}
.member-filter-select:focus {
  box-shadow: none;
  border: none;
  outline: none;
  background: transparent;
}
.stat-icon-filter {
  background: linear-gradient(195deg, #7c4dff, #4527a0);
}
.stat-icon-sprint {
  background: linear-gradient(195deg, #17c1e8, #0c7a9a);
}
.sprint-card {
  background: linear-gradient(135deg, #f0f9fc 0%, #e0f3fa 100%);
  border-color: #b8e3f0;
}
.sprint-chevron {
  color: #17c1e8 !important;
}
.sprint-filter-select-mgr:hover {
  color: #17c1e8;
}
.stat-card {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 12px;
  padding: 0.75rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  box-shadow: var(--shadow-card);
  height: 100%;
  /* Allow the BaseSelect popup to escape the card. */
  overflow: visible;
}
.filter-card {
  position: relative;
  z-index: 2;
}
.stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.stat-icon-total {
  background: linear-gradient(195deg, #42424a, #191919);
}
.stat-icon-overdue {
  background: linear-gradient(195deg, #ef5350, #e53935);
}
.stat-icon-todo {
  background: linear-gradient(195deg, #17c1e8, #0c7a9a);
}
.stat-icon-inprogress {
  background: linear-gradient(195deg, #4361ee, #2d4bc8);
}
.stat-icon-done {
  background: linear-gradient(195deg, #82d616, #17ad37);
}
.member-filter-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}
.member-filter-chevron {
  position: absolute;
  right: 0.4rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.75rem;
  color: #7c4dff;
  pointer-events: none;
}
.member-filter-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: none;
  width: 100%;
}
.member-filter-select:hover {
  color: #7c4dff;
}
.stat-label {
  font-size: 0.68rem;
  color: var(--text-secondary);
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 0.05rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.stat-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.1;
}
.due-overdue {
  color: #e74c3c !important;
  font-weight: 600;
}
.task-type-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.15rem 0.55rem;
  border-radius: 50px;
  text-transform: uppercase;
}
.badge-plan {
  background: #f4eeff;
  color: #7c4dff;
  border: 1px solid #d5b8ff;
}
.badge-run {
  background: #e8f4fd;
  color: #1a73e8;
  border: 1px solid #bee5eb;
}
.task-progress-track {
  height: 6px;
  border-radius: 3px;
  background: #e9ecef;
  overflow: hidden;
}
.task-progress-bar {
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, #49a3f1, #1a73e8);
  transition: width 0.3s ease;
}
</style>
